import { Button } from '@/shared/components/ui/button';
import { MessageCircle, Clock } from 'lucide-react';
import type { Client } from '@/features/client-relationships';
import { formatLastLogin } from '@/shared/utils/date-formatter';

type SPConnectedClientsListProps = {
  clients: Client[];
  onMessageClick?: (clientId: string) => void;
};

const getInitials = (firstName: string, lastName?: string): string => {
  const first = firstName.charAt(0);
  const last = lastName ? lastName.charAt(0) : '';
  return `${first}${last}`.toUpperCase();
};

export function SPConnectedClientsList({
  clients,
  onMessageClick,
}: SPConnectedClientsListProps) {
  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-3">
        Connected Clients ({clients.length})
      </h4>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {clients.map((client) => {
          const fullName = [client.firstName, client.lastName].filter(Boolean).join(' ');

          return (
            <div
              key={client.id}
              className="p-4 border border-gray-200 rounded-lg hover:border-[#026857] transition-colors"
            >
              <div className="flex items-start gap-3 mb-3">
                {client.profilePhoto ? (
                  <img
                    src={client.profilePhoto}
                    alt={fullName}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#026857]/10 flex items-center justify-center">
                    <span className="text-[#026857] font-semibold text-sm">
                      {getInitials(client.firstName, client.lastName)}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900 truncate">
                    {fullName}
                  </h4>
                  <p className="text-xs text-gray-500 truncate">
                    {client.email}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onMessageClick?.(client.id)}
                  className="h-7 px-2 text-xs text-gray-600 hover:text-[#026857] hover:bg-[#026857]/5"
                >
                  <MessageCircle className="w-3 h-3 mr-1" />
                  Message
                </Button>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-xs">
                  Last login:{' '}
                  <span className="font-medium">
                    {formatLastLogin(client.lastLoginAt ?? null)}
                  </span>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
